import { useCallback, useState } from 'react';

/** The console's theme: forced light or dark, or the OS's (`system`). Kept in this browser only. */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'zchat-hub.theme';

function stored(): Theme {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function apply(theme: Theme): void {
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

/** Puts the saved theme on <html> before the first paint (the embedded inbox sets its own). */
export function applyStoredTheme(): void {
  apply(stored());
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(stored);
  const setTheme = useCallback((next: Theme) => {
    try {
      if (next === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // Storage off (private window): the choice lasts for this page only.
    }
    apply(next);
    setThemeState(next);
  }, []);
  return [theme, setTheme];
}
